import React, { useState } from 'react';
import classes from './profile.module.scss';
import { GET_USER } from '../../queries';
import { CircularProgress, Button } from '@material-ui/core';
import { Query, Mutation } from 'react-apollo';
import Form, { IState } from '../form/form';
import gql from 'graphql-tag';
import {
  required,
  minLength,
  passwordConfirm
} from '../../validators/form-validators';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { uploadFile } from '../add-product';
import Input from '../input/input';
import { navigate } from '@reach/router';
import SnackbarMessage from '../snackbar/snackbar';
import { MAX_IMAGE_FILE_SIZE, SUPPORTED_FORMATS } from '../../constants';

const UPDATE_USER = gql`
  mutation UpdateUser(
    $firstName: String!
    $lastName: String!
    $username: String!
    $bio: String!
    $image: String!
  ) {
    updateUser(
      firstName: $firstName
      lastName: $lastName
      username: $username
      bio: $bio
      image: $image
    )
  }
`;

const CHANGE_PASSWORD_MUTATION = gql`
  mutation changePassword($password: String!, $oldPassword: String!) {
    changePassword(password: $password, oldPassword: $oldPassword)
  }
`;

const stateChangeForm: IState = {
  oldPassword: {
    label: 'Старий пароль',
    config: {
      type: 'password',
      placeholder: 'Введіть свій старий пароль'
    },
    value: '',
    validators: [required, minLength(6)],
    errorMessage: ''
  },
  password: {
    label: 'Новий пароль',
    config: {
      type: 'password',
      placeholder: 'Введіть свій новий пароль'
    },
    value: '',
    validators: [required, minLength(6)],
    errorMessage: ''
  },
  confirmPassword: {
    label: 'Повторіть новий пароль',
    config: {
      type: 'password',
      placeholder: 'Повторіть свій новий пароль'
    },
    dependency: 'password',
    value: '',
    validators: [required, minLength(6), passwordConfirm],
    errorMessage: ''
  }
};

const Profile = () => {
  const [userImage, setUserImage]: any = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [newImageUrls, setNewImageUrls] = useState('');
  const [snackbar, snackbarOpen] = useState({
    show: false,
    message: '',
    status: ''
  });

  function openSnackbar(status: string, message: string) {
    snackbarOpen({ show: true, status, message });
  }

  function onImageLoad(event, handleChange) {
    const file = event.target.files[0];
    if (file.size > MAX_IMAGE_FILE_SIZE) {
      openSnackbar('error', 'Виберіть фото розміром до 1 Мб');
    } else if (!SUPPORTED_FORMATS.includes(file.type)) {
      openSnackbar('error', 'Виберіть фото у форматі .JPG або .PNG');
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setNewImageUrls(reader.result as string);
        setUserImage(file);
      };
      handleChange(event);
    }
  }

  function getFormInput(
    key,
    { values, errors, touched, handleChange, handleBlur }
  ) {
    const labels = {
      firstName: `Ім'я`,
      lastName: 'Прізвище',
      username: 'Логін',
      bio: 'Інформація про мене'
    };
    return (
      <Input
        label={labels[key]}
        name={key}
        value={values[key]}
        error={touched[key] && errors[key]}
        changed={handleChange}
        unfocused={handleBlur}
      />
    );
  }

  function upload(file) {
    return uploadFile(file).then((uploadInfo: any) => uploadInfo.secure_url);
  }

  async function updateUser(formValues, userMutation) {
    const { firstName, lastName, username, bio, image } = formValues;
    setShowSpinner(true);
    userMutation({
      variables: {
        firstName,
        lastName,
        username,
        bio,
        image: userImage && userImage.type ? await upload(userImage) : image
      }
    })
      .then(() => {
        openSnackbar('success', 'Ваші дані збережено');
        setShowSpinner(false);
        setTimeout(() => navigate('/'), 1000);
      })
      .catch(e => {
        setShowSpinner(false);
        if (e.graphQLErrors && e.graphQLErrors.length) {
          openSnackbar('error', e.graphQLErrors[0].message);
        }
      });
  }

  return (
    <Query query={GET_USER}>
      {({ loading, error, data }) => {
        if (loading) {
          return (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
            </div>
          );
        }
        if (error) {
          return <div>Error fetching user profile</div>;
        }
        const user = data && data.me;
        if (!user && !snackbar.show) {
          openSnackbar('error', 'Немає такого користувача');
          localStorage.removeItem('token');
        }
        if (!userImage && user) {
          setUserImage(user.image);
        }

        return (
          <>
            <SnackbarMessage snackbar={snackbar} />
            {user && (
              <div className={classes.root}>
                <div className={`${classes.inputBox} ${classes.topContainer}`}>
                  <Formik
                    initialValues={{
                      firstName: user.firstName,
                      lastName: user.lastName,
                      username: user.username,
                      bio: user.bio,
                      image: user.image
                    }}
                    onSubmit={(values, { setSubmitting }) => {
                      setTimeout(() => {
                        alert(JSON.stringify(values, null, 2));
                        setSubmitting(false);
                      }, 500);
                    }}
                    validationSchema={Yup.object().shape({
                      firstName: Yup.string().required(`Введіть своє ім'я`),
                      lastName: Yup.string().required('Введіть своє прізвище'),
                      username: Yup.string()
                        .matches(/^[0-9a-z_.-]+$/, {
                          message:
                            'Використовуйте тільки малі літери, цифри і знаки . - _'
                        })
                        .max(20)
                        .required('Введіть логін'),
                      bio: Yup.string().required('Розкажіть щось про себе'),
                      image: Yup.string().required(
                        'Виберіть фото розміром до 1 Мб'
                      )
                    })}
                  >
                    {props => {
                      const {
                        values,
                        handleChange,
                        handleReset,
                        isValid
                      } = props;

                      return (
                        <form>
                          <p className={classes.imgLabel}>
                            Виберіть фото розміром до 1 МБ
                          </p>
                          <div className={classes.userImg}>
                            <img
                              src={
                                newImageUrls ||
                                user.image ||
                                '/assets/profile_default.png'
                              }
                              alt="Profile Image"
                            />
                            <div className={classes.changePhoto}>
                              <input
                                className={classes.changePhotoInput}
                                type="file"
                                name="image"
                                value=""
                                id="image"
                                onChange={e => onImageLoad(e, handleChange)}
                              />
                              <label
                                data-e2e-id="change-photo-btn"
                                className={classes.changePhotoLabel}
                                htmlFor="image"
                              >
                                <span>Змінити</span>
                              </label>
                            </div>
                          </div>

                          {getFormInput('firstName', props)}
                          {getFormInput('lastName', props)}
                          {getFormInput('username', props)}
                          {getFormInput('bio', props)}

                          <div style={{ marginTop: '30px' }}>
                            <Mutation
                              mutation={UPDATE_USER}
                              refetchQueries={[{ query: GET_USER }]}
                            >
                              {updateUserMutation => {
                                return (
                                  <div className={classes.btnsContainer}>
                                    <Button
                                      color="secondary"
                                      variant="contained"
                                      disabled={!isValid}
                                      data-e2e-id={'save-user-data-btn'}
                                      style={{ textTransform: 'capitalize' }}
                                      onClick={() =>
                                        updateUser(
                                          {
                                            firstName: values.firstName,
                                            lastName: values.lastName,
                                            username: values.username,
                                            bio: values.bio,
                                            image: values.image
                                          },
                                          updateUserMutation
                                        )
                                      }
                                    >
                                      Зберегти
                                      {showSpinner && (
                                        <CircularProgress
                                          size={20}
                                          color={'inherit'}
                                          style={{ marginLeft: '10px' }}
                                        />
                                      )}
                                    </Button>
                                    <Button
                                      color="secondary"
                                      variant="outlined"
                                      data-e2e-id="cancel-user-data-btn"
                                      style={{ textTransform: 'capitalize' }}
                                      disabled={!isValid}
                                      onClick={() => handleReset()}
                                    >
                                      Відмінити
                                    </Button>
                                  </div>
                                );
                              }}
                            </Mutation>
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
                <div className={classes.container}>
                  <h3 className={classes.enterDetailsText}>
                    Змінити пароль до акаунту:
                  </h3>

                  <Form
                    state={stateChangeForm}
                    mutation={CHANGE_PASSWORD_MUTATION}
                    submitOptions={{
                      keys: ['oldPassword', 'password'],
                      label: 'Зберегти',
                      id: 'save-new-password',
                      redirectTo: '/'
                    }}
                  />
                </div>
              </div>
            )}
          </>
        );
      }}
    </Query>
  );
};

export default Profile;
